/**
 * Traductions FR du vocabulaire de l'audit trail V2.
 *
 * Les valeurs brutes (actions, alias d'entités) viennent des enums V2
 * (AuditActionEnum, attributs #[Auditable(alias:)]) : toute valeur inconnue
 * est affichée telle quelle en fallback — l'ajout d'une entité auditée côté
 * V2 ne casse donc rien ici, elle apparaît juste non traduite.
 */

export const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Création',
  UPDATE: 'Modification',
  DELETE: 'Suppression',
  VALIDATE: 'Validation',
  REJECT: 'Rejet',
  SYNC: 'Synchronisation',
  ARCHIVE: 'Archivage',
  ACTIVATE: 'Activation',
  DEACTIVATE: 'Désactivation',
  CANCEL: 'Annulation',
  LINK: 'Association',
  UNLINK: 'Dissociation',
  REORDER: 'Réorganisation',
  DUPLICATE: 'Duplication',
  SEND: 'Envoi',
  DOWNLOAD: 'Téléchargement',
  PROCESS: 'Traitement',
  BULK_UPDATE: 'Modification en masse',
};

/** Classes de badge par action (fallback gris pour une action inconnue). */
export const ACTION_BADGE_CLASSES: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  UPDATE: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  DELETE: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  VALIDATE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  REJECT: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  SYNC: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  ARCHIVE: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  ACTIVATE: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',
  DEACTIVATE: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  CANCEL: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  LINK: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
  UNLINK: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  SEND: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  BULK_UPDATE: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
};

export const ACTION_BADGE_FALLBACK =
  'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300';

/** Pastilles du fil d'activité (classes littérales pour le JIT Tailwind). */
export const ACTION_DOT_CLASSES: Record<string, string> = {
  CREATE: 'bg-green-500',
  UPDATE: 'bg-blue-500',
  DELETE: 'bg-red-500',
  VALIDATE: 'bg-emerald-500',
  REJECT: 'bg-rose-500',
  SYNC: 'bg-cyan-500',
  ARCHIVE: 'bg-amber-500',
  ACTIVATE: 'bg-teal-500',
  DEACTIVATE: 'bg-orange-500',
  CANCEL: 'bg-orange-500',
  LINK: 'bg-violet-500',
  UNLINK: 'bg-purple-500',
  SEND: 'bg-indigo-500',
  BULK_UPDATE: 'bg-sky-500',
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function actionBadgeClass(action: string): string {
  return ACTION_BADGE_CLASSES[action] ?? ACTION_BADGE_FALLBACK;
}

export function actionDotClass(action: string): string {
  return ACTION_DOT_CLASSES[action] ?? 'bg-gray-400';
}

/** Alias V2 (#[Auditable(alias:)]) → libellé FR. */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
  school: 'École',
  establishment: 'Établissement',
  level: 'Niveau',
  cycle: 'Cycle',
  period: 'Période',
  room: 'Salle',
  room_type: 'Type de salle',
  school_period: 'Année scolaire',
  school_class: 'Classe',
  school_class_student: 'Élève de classe',
  school_class_teacher: 'Enseignant de classe',
  subject: 'Matière',
  subject_group: 'Groupe de matières',
  level_subject: 'Matière de niveau',
  user_group: "Groupe d'utilisateurs",
  user_role: 'Utilisateur',
  role: 'Rôle',
  permission: 'Permission',
  role_permission: 'Permission de rôle',
  relation_type: 'Type de relation',
  user_relationship: 'Relation entre utilisateurs',
  student: 'Élève',
  schooling_record: 'Dossier de scolarité',
  teacher_replacement: 'Remplacement enseignant',
  punishment: 'Punition',
  punishment_type: 'Type de punition',
  holiday: 'Vacances',
  setting: 'Paramètre',
  skill: 'Compétence',
  skill_domain: 'Domaine de compétences',
  skill_subdomain: 'Sous-domaine de compétences',
  skill_family: 'Famille de compétences',
  skill_scale: 'Échelle de compétences',
  honor: 'Distinction',
  grading_system: 'Système de notation',
  evaluation_type: "Type d'évaluation",
  absence: 'Absence',
  absence_type: "Type d'absence",
  agenda: 'Agenda',
  event_type: "Type d'événement",
  attendance_status: 'Statut de présence',
  distribution_list: 'Liste de diffusion',
  message_template: 'Modèle de message',
  messaging_label: 'Étiquette de messagerie',
  attribute_type: "Type d'attribut",
  billing_supplier: 'Fournisseur (facturation)',
  billing_payer: 'Payeur (facturation)',
  document_type: 'Type de document',
  activity_category: "Catégorie d'activité",
  montessori_color_bucket: 'Couleur Montessori',
};

export function entityTypeLabel(entityType: string): string {
  return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}

/**
 * Verbes pour la lecture narrative : « {Utilisateur} {verbe} {objet} ».
 */
export const ACTION_VERBS: Record<string, string> = {
  CREATE: 'a créé',
  UPDATE: 'a modifié',
  DELETE: 'a supprimé',
  VALIDATE: 'a validé',
  REJECT: 'a rejeté',
  SYNC: 'a synchronisé',
  ARCHIVE: 'a archivé',
  ACTIVATE: 'a activé',
  DEACTIVATE: 'a désactivé',
  CANCEL: 'a annulé',
  LINK: 'a associé',
  UNLINK: 'a dissocié',
  REORDER: 'a réorganisé',
  DUPLICATE: 'a dupliqué',
  SEND: 'a envoyé',
  DOWNLOAD: 'a téléchargé',
  PROCESS: 'a traité',
  BULK_UPDATE: 'a modifié en masse',
};

export function actionVerb(action: string): string {
  return ACTION_VERBS[action] ?? `a effectué « ${action.toLowerCase()} » sur`;
}

interface EntityPhrase {
  /** Nom commun en minuscules (« élève », « classe »…) */
  noun: string;
  /** Article défini accolé au nom : « l'élève », « la classe » */
  definite: string;
  /** Article indéfini : « un élève », « une classe » (fallback sans nom) */
  indefinite: string;
  /** Personne physique → « la fiche de Martin Dupont » */
  person?: boolean;
}

const p = (noun: string, definite: string, indefinite: string, person?: boolean): EntityPhrase => ({
  noun,
  definite,
  indefinite,
  person,
});

/** Tournures françaises par type d'objet (fallback générique sinon). */
export const ENTITY_PHRASES: Record<string, EntityPhrase> = {
  student: p('élève', "l'élève", 'un élève', true),
  user_role: p('utilisateur', "l'utilisateur", 'un utilisateur', true),
  teacher_replacement: p('remplacement enseignant', 'le remplacement enseignant', 'un remplacement enseignant'),
  user_relationship: p('relation entre utilisateurs', 'la relation entre utilisateurs', 'une relation entre utilisateurs'),
  school: p('école', "l'école", 'une école'),
  establishment: p('établissement', "l'établissement", 'un établissement'),
  level: p('niveau', 'le niveau', 'un niveau'),
  cycle: p('cycle', 'le cycle', 'un cycle'),
  period: p('période', 'la période', 'une période'),
  school_period: p('année scolaire', "l'année scolaire", 'une année scolaire'),
  school_class: p('classe', 'la classe', 'une classe'),
  school_class_student: p('affectation élève-classe', "l'affectation élève-classe", 'une affectation élève-classe'),
  school_class_teacher: p('affectation enseignant-classe', "l'affectation enseignant-classe", 'une affectation enseignant-classe'),
  subject: p('matière', 'la matière', 'une matière'),
  subject_group: p('groupe de matières', 'le groupe de matières', 'un groupe de matières'),
  level_subject: p('matière de niveau', 'la matière de niveau', 'une matière de niveau'),
  user_group: p("groupe d'utilisateurs", "le groupe d'utilisateurs", "un groupe d'utilisateurs"),
  role: p('rôle', 'le rôle', 'un rôle'),
  permission: p('permission', 'la permission', 'une permission'),
  role_permission: p('permission de rôle', 'la permission de rôle', 'une permission de rôle'),
  relation_type: p('type de relation', 'le type de relation', 'un type de relation'),
  schooling_record: p('dossier de scolarité', 'le dossier de scolarité', 'un dossier de scolarité'),
  punishment: p('punition', 'la punition', 'une punition'),
  punishment_type: p('type de punition', 'le type de punition', 'un type de punition'),
  holiday: p('période de vacances', 'la période de vacances', 'une période de vacances'),
  setting: p('paramètre', 'le paramètre', 'un paramètre'),
  skill: p('compétence', 'la compétence', 'une compétence'),
  skill_domain: p('domaine de compétences', 'le domaine de compétences', 'un domaine de compétences'),
  skill_subdomain: p('sous-domaine de compétences', 'le sous-domaine de compétences', 'un sous-domaine de compétences'),
  skill_family: p('famille de compétences', 'la famille de compétences', 'une famille de compétences'),
  skill_scale: p('échelle de compétences', "l'échelle de compétences", 'une échelle de compétences'),
  honor: p('distinction', 'la distinction', 'une distinction'),
  grading_system: p('système de notation', 'le système de notation', 'un système de notation'),
  evaluation_type: p("type d'évaluation", "le type d'évaluation", "un type d'évaluation"),
  absence: p('absence', "l'absence", 'une absence'),
  absence_type: p("type d'absence", "le type d'absence", "un type d'absence"),
  agenda: p("événement d'agenda", "l'événement d'agenda", "un événement d'agenda"),
  event_type: p("type d'événement", "le type d'événement", "un type d'événement"),
  attendance_status: p('statut de présence', 'le statut de présence', 'un statut de présence'),
  room: p('salle', 'la salle', 'une salle'),
  room_type: p('type de salle', 'le type de salle', 'un type de salle'),
  distribution_list: p('liste de diffusion', 'la liste de diffusion', 'une liste de diffusion'),
  message_template: p('modèle de message', 'le modèle de message', 'un modèle de message'),
  messaging_label: p('étiquette de messagerie', "l'étiquette de messagerie", 'une étiquette de messagerie'),
  attribute_type: p("type d'attribut", "le type d'attribut", "un type d'attribut"),
  billing_supplier: p('fournisseur', 'le fournisseur', 'un fournisseur'),
  billing_payer: p('payeur', 'le payeur', 'un payeur'),
  document_type: p('type de document', 'le type de document', 'un type de document'),
  activity_category: p("catégorie d'activité", "la catégorie d'activité", "une catégorie d'activité"),
  montessori_color_bucket: p('couleur Montessori', 'la couleur Montessori', 'une couleur Montessori'),
};

/**
 * Tournure « objet » de la phrase :
 *  - personne avec nom → « la fiche de Martin Dupont »
 *  - objet avec nom    → « la classe « CM2-B » »
 *  - sans nom          → « un élève », « une classe »… (l'UUID reste en détail)
 */
export function entityPhrase(entityType: string, entityLabel: string | null | undefined): string {
  const phrase = ENTITY_PHRASES[entityType];
  if (!phrase) {
    const generic = entityTypeLabel(entityType);
    return entityLabel ? `${generic} « ${entityLabel} »` : `un élément « ${generic} »`;
  }
  if (!entityLabel) {
    return phrase.indefinite;
  }
  if (phrase.person) {
    return `la fiche de ${entityLabel}`;
  }
  return `${phrase.definite} « ${entityLabel} »`;
}

export const ACTOR_TYPE_LABELS: Record<string, string> = {
  USER: 'Utilisateur',
  SYSTEM: 'Système',
  SCHEDULER: 'Tâche planifiée',
  CONNECTOR: 'Connecteur',
};

export function actorTypeLabel(type: string): string {
  return ACTOR_TYPE_LABELS[type] ?? type;
}

export const ORIGIN_LABELS: Record<string, string> = {
  HTTP: 'Web / API',
  MESSENGER: 'Traitement asynchrone',
  COMMAND: 'Ligne de commande',
  WEBHOOK: 'Webhook',
};

export function originLabel(origin: string): string {
  return ORIGIN_LABELS[origin] ?? origin;
}

// ----- Formatage des dates ------------------------------------------------

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  });
}

export function formatRelativeDate(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'hier';
  if (days < 30) return `il y a ${days} j`;
  return new Date(iso).toLocaleDateString('fr-FR');
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Valeur de diff → texte lisible (support). Jamais d'UUID en première
 *  lecture : une référence brute devient « (référence) », le détail technique
 *  reste accessible via la vue développeur. */
export function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'object') {
    // Relation sérialisée {id, label} : le label suffit en lecture support,
    // l'id reste accessible via la vue développeur (JSON complet).
    const label = (value as { label?: unknown }).label;
    if (typeof label === 'string' && label !== '') return label;
    return JSON.stringify(value);
  }
  const text = String(value);
  return UUID_RE.test(text) ? '(référence)' : text;
}

/**
 * Changements dignes d'être montrés en première lecture : on écarte les
 * lignes sans changement visible (avant = après une fois formaté, ex. deux
 * références techniques) — le compte réel reste visible via le détail.
 */
export function meaningfulDiff<T extends { before: unknown; after: unknown }>(diff: T[]): T[] {
  return diff.filter((change) => formatDiffValue(change.before) !== formatDiffValue(change.after));
}
